import React, { useMemo, useState } from 'react';
import { createAttempt, submitReading } from '../../api/exams';
import { setPartProgress, computeMistakes, scoreReadingPart } from './_progress';
import { T } from './_theme';
import {
  ReadingFrame, PartTopBar, OptionBtn, FeedbackCard, PrimaryBtn, GhostBtn,
  NudgeBanner, readArabicPref, readingHomeHref,
} from './_primitives';

// FET Reading — Part 5 · Long Reading
//
// Content shape:
//   { article: { title, byline, paragraphs: [{ id, text }] },
//     questions: [{
//       n, text,
//       options: [{letter, text}],
//       correct, focus_paragraph, highlight,
//       why_en, why_ar, mistake_en, mistake_ar, evidence,
//       examples: [{good|bad, text}]
//     }] }
// Submission key per question: q_{i} → letter

export default function Part5Screen({
  isMobile, navigate, exam, loading,
  onContinueToNextPart, nextPartLabel,
}) {
  const part = useMemo(
    () => (exam?.reading_parts || []).find((p) => p.part_number === 5),
    [exam],
  );
  const article = part?.content?.article || {};
  const paragraphs = useMemo(() => Array.isArray(article.paragraphs) ? article.paragraphs : [], [article]);
  const questions = useMemo(() => (part?.content?.questions || []).filter(Boolean), [part]);
  const total = questions.length;

  const [arabicOn, setArabicOn] = useState(readArabicPref);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showFull, setShowFull] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState('');

  const persistArabic = (next) => {
    setArabicOn(next);
    try { window.localStorage.setItem('fet_reading_arabic', next ? '1' : '0'); } catch {}
  };

  if (loading || !exam) {
    return <ReadingFrame isMobile={isMobile}><div style={{ padding: 32, color: T.ink2, fontFamily: T.sans }}>Loading reading exam…</div></ReadingFrame>;
  }
  if (!part || !total || !paragraphs.length) {
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={5} partTitle="Long Reading" currentIdx={0} total={1} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24, color: T.ink2, fontFamily: T.sans, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: T.ink }}>Part 5 isn't ready</div>
          <div>An admin needs to add an article + at least one MCQ question.</div>
        </div>
      </ReadingFrame>
    );
  }

  if (finalResult) {
    const pct = finalResult.max ? Math.round((finalResult.score / finalResult.max) * 100) : 0;
    return (
      <ReadingFrame isMobile={isMobile}>
        <PartTopBar partNumber={5} partTitle="Long Reading" currentIdx={total - 1} total={total} onBack={() => navigate(readingHomeHref())} />
        <div style={{ padding: 24 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.hairline}`, borderRadius: 18, padding: 22, textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: T.ink3, marginBottom: 12 }}>Part 5 result</div>
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 48, fontWeight: 700, color: T.blue, fontVariantNumeric: 'tabular-nums' }}>{finalResult.score}</span>
              <span style={{ fontSize: 20, color: T.ink3, fontWeight: 600 }}>/ {finalResult.max}</span>
            </div>
            <div style={{ fontSize: 14, color: T.ink2, marginTop: 8 }}>You scored <strong style={{ color: T.ink }}>{pct}%</strong>.</div>
            <div style={{ marginTop: 18, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <GhostBtn onClick={() => { setAnswers({}); setCurrentIdx(0); setFinalResult(null); setError(''); }}>Try again</GhostBtn>
              {onContinueToNextPart ? (
                <PrimaryBtn full={false} onClick={onContinueToNextPart}>{nextPartLabel || 'Next part →'}</PrimaryBtn>
              ) : (
                // No next part configured — finishing this screen would
                // otherwise dead-end. Send the student back to the
                // dashboard so they can pick the next exam (or re-enter
                // reading home if they want another attempt).
                <PrimaryBtn full={false} onClick={() => navigate('/dashboard')}>Back to dashboard</PrimaryBtn>
              )}
            </div>
          </div>
        </div>
      </ReadingFrame>
    );
  }

  const q = questions[currentIdx] || {};
  const opts = Array.isArray(q.options) ? q.options : [];
  const correctLetter = String(q.correct || '').toUpperCase();
  const picked = answers[`q_${currentIdx}`] || null;
  const answered = picked !== null;
  const isCorrect = answered && picked === correctLetter;
  const isLast = currentIdx === total - 1;

  const pickAnswer = (letter) => {
    if (answered) return;
    setAnswers((prev) => ({ ...prev, [`q_${currentIdx}`]: letter }));
  };

  const goNext = async () => {
    if (!isLast) {
      setCurrentIdx((i) => Math.min(total - 1, i + 1));
      setShowFull(false);
      return;
    }
    setSubmitting(true);
    setError('');
    const local = scoreReadingPart(5, part?.content, answers);
    const result = {
      score: local.score,
      max: local.max || total,
      mistakes: computeMistakes(5, part?.content, answers),
    };
    setFinalResult(result);
    setPartProgress(5, result);
    try {
      const attempt = await createAttempt(exam.id, 'practice', 'reading');
      await submitReading(attempt.id, { '5': answers });
    } catch (err) {
      const apiErr = err?.response?.data;
      setError(apiErr?.error || 'Could not save your attempt to the server. Your local score is shown.');
    } finally {
      setSubmitting(false);
    }
  };

  const focusId = q.focus_paragraph;
  const focusPara = focusId ? paragraphs.find((p) => p.id === focusId) : null;
  const visibleParas = showFull ? paragraphs : (focusPara ? [focusPara] : paragraphs.slice(0, 1));

  // Render a single paragraph, highlighting the q.highlight substring inline.
  const renderPara = (p) => {
    const text = p.text || '';
    const hl = q.highlight || '';
    if (!hl || !text.includes(hl)) {
      return (
        <div key={p.id} style={{
          padding: '10px 12px',
          background: focusPara && p.id === focusPara.id ? T.blueBg : 'transparent',
          borderRadius: 8,
          border: focusPara && p.id === focusPara.id ? `1px solid ${T.blueEdge}` : 'none',
          fontSize: 14, lineHeight: 1.75, fontFamily: T.serif, color: T.ink,
          marginBottom: 8,
        }}>{text}</div>
      );
    }
    const [before, ...rest] = text.split(hl);
    const after = rest.join(hl);
    return (
      <div key={p.id} style={{
        padding: '10px 12px',
        background: T.blueBg, borderRadius: 8, border: `1px solid ${T.blueEdge}`,
        fontSize: 14, lineHeight: 1.75, fontFamily: T.serif, color: T.ink,
        marginBottom: 8,
      }}>
        {before}
        <mark style={{ background: '#FAE9B4', color: T.ink, padding: '0 2px', borderRadius: 2 }}>{hl}</mark>
        {after}
      </div>
    );
  };

  return (
    <ReadingFrame isMobile={isMobile}>
      <PartTopBar partNumber={5} partTitle="Long Reading" currentIdx={currentIdx} total={total} onBack={() => navigate(readingHomeHref())} />
      <div style={{ flex: 1, padding: 16 }}>
        {/* Question card */}
        <div style={{ padding: '12px 14px', background: T.surface, borderRadius: 14, border: `1px solid ${T.hairline}`, marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.ink3, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 2 }}>{q.n || currentIdx + 1}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{q.text}</div>
        </div>

        {/* Article */}
        <div style={{ background: T.surface, borderRadius: 14, border: `1px solid ${T.hairline}`, padding: 14 }}>
          {article.title && (
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: -0.3, marginBottom: 2, fontFamily: T.serif, color: T.ink }}>{article.title}</div>
          )}
          {article.byline && (
            <div style={{ fontSize: 12, color: T.ink3, fontStyle: 'italic', marginBottom: 12, fontFamily: T.serif }}>{article.byline}</div>
          )}
          <div>{visibleParas.map(renderPara)}</div>
          {paragraphs.length > visibleParas.length && (
            <button
              type="button"
              onClick={() => setShowFull(true)}
              style={{
                marginTop: 8, fontSize: 12, fontWeight: 600,
                color: T.blue, padding: '6px 10px',
                background: T.blueBg, border: `1px solid ${T.blueEdge}`,
                borderRadius: 9999, cursor: 'pointer', fontFamily: T.sans,
              }}
            >+ Show full article</button>
          )}
          {showFull && paragraphs.length > 1 && (
            <button
              type="button"
              onClick={() => setShowFull(false)}
              style={{
                marginTop: 8, fontSize: 12, fontWeight: 600,
                color: T.ink3, padding: '6px 10px',
                background: T.surface, border: `1px solid ${T.hairline}`,
                borderRadius: 9999, cursor: 'pointer', fontFamily: T.sans,
              }}
            >− Collapse to focus paragraph</button>
          )}
        </div>

        {/* Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
          {opts.map((o) => {
            const letter = String(o.letter || '').toUpperCase();
            const text = typeof o === 'string' ? o : (o.text || '');
            let state = 'idle';
            if (answered) {
              if (letter === correctLetter) state = 'correct';
              else if (letter === picked) state = 'incorrect';
              else state = 'dim';
            }
            return (
              <OptionBtn key={letter} letter={letter} state={state} onClick={() => pickAnswer(letter)}>
                {text}
              </OptionBtn>
            );
          })}
        </div>

        {answered && (
          <FeedbackCard
            correct={isCorrect}
            header={isCorrect ? `Nice — ${correctLetter} is correct` : `Almost — the answer is ${correctLetter}`}
            picked={picked}
            lang={arabicOn ? 'ar' : 'en'}
            onLangChange={(v) => persistArabic(v === 'ar')}
            simple={arabicOn ? q.why_ar : q.why_en}
            mistake={!isCorrect ? (arabicOn ? q.mistake_ar : q.mistake_en) : null}
            examples={Array.isArray(q.examples) ? q.examples : null}
            evidence={q.evidence}
          />
        )}

        {error && <div style={{ marginTop: 12, fontSize: 13, color: T.miss }}>{error}</div>}
        {!answered && <div style={{ marginTop: 14 }}><NudgeBanner>Pick an answer above to continue.</NudgeBanner></div>}
      </div>

      <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.hairline}`, background: T.surface, display: 'flex', gap: 10 }}>
        <GhostBtn disabled={currentIdx === 0} onClick={() => { setCurrentIdx((i) => Math.max(0, i - 1)); setShowFull(false); }}>← Previous</GhostBtn>
        <div style={{ flex: 1 }} />
        <PrimaryBtn full={false} disabled={!answered || submitting} onClick={goNext}>
          {submitting ? 'Saving…' : isLast ? 'Finish part →' : 'Next question →'}
        </PrimaryBtn>
      </div>
    </ReadingFrame>
  );
}
